import {
    LocationSearchLocationLocalized,
    LocationSearchResultItem,
    RequestForLocationSearch,
    ResponseForLocationSearch
} from "@afidium/ts-facade";
import {
    ChangeDetectorRef,
    Component,
    EventEmitter, HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from "@angular/forms";
import { faCity, faPlane, faPlaneArrival, faPlaneDeparture, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { Yii } from 'libraries/interfaces/Yii';
import { AutocompleteComponent } from 'libraries/module/formbuilder/fields/autocomplete/autocomplete.component';
import { IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { IMultiselectOptions } from "libraries/module/formbuilder/fields/multiselect/multiselect.model";
import { TLocalizedString } from "libraries/pipes/localized/localized.interface";
import { LocalizedPipe } from "libraries/pipes/localized/localized.pipe";
import {
    DictionarySearchRequestService
} from "libraries/service/resources/dictionary-search/dictionary-search-request.service";
import {
    DictionarySearchResourceService
} from "libraries/service/resources/dictionary-search/dictionary-search-resource.service";
import { BehaviorSubject, Observable, of } from "rxjs";
import { debounceTime, filter, map, switchMap, tap } from "rxjs/operators";


export interface IAirportSelectOutput {
    code: string;
    isAirportOnly: boolean;
}

@Component({
    selector: 'airport-select',
    template: `
        <div class="readonly-airport-value"
             *ngIf="readonlyIfSet && readonlyValue?.length > 0; else autocompleteContainer">
            <fa-icon [icon]="iconDefinition"></fa-icon>
            <span>{{ readonlyValue }}</span>
        </div>
        <ng-template #autocompleteContainer>
            <autocomplete #autocomplete
                          [fieldConfig]="fieldConfig"
                          [options]="filteredOptions"
                          [autoActiveFirstOption]="true"
                          [delayBlur]="false"
                          [autocompleteTransparent]="false"
                          [allowSelectOnBlur]="true"
                          [displayFn]="displayDestination.bind(this)"
                          (onInputBlur)="emitValueIfNoPanel($event)"
                          (onChangeInput)="changeInput($event)">
                <ng-template let-option>
                    <div class="option-item" [class.is-child]="option.isChild">
                        <div *ngIf="option.faIcon">
                            <fa-icon [icon]="option.faIcon"></fa-icon>
                        </div>
                        <div class="text-content">
                            <div>
                                {{option.label}}
                            </div>
                            <div *ngIf="option.secondLine" class="second-line">
                                {{option.secondLine}}
                            </div>
                        </div>
                    </div>
                </ng-template>
            </autocomplete>
        </ng-template>
    `,
    styleUrls: ["airport-select.component.scss"]
})

export class AirportSelectComponent implements OnInit, OnChanges {

    @Input()
    public firstLocationCode: string;
    @Input()
    public firstIsAirportOnly: boolean;
    @Input()
    public direction: string = 'from';
    @Input()
    public readonlyIfSet: boolean = false;
    @Input()
    public customizedIconDefinition: { from: IconDefinition, to: IconDefinition };
    @Input()
    public tabindex: number;
    @Input()
    public fieldConfig?: IFieldSpecs;
    @Output()
    public readonly onChooseLocation: EventEmitter<IAirportSelectOutput> = new EventEmitter<IAirportSelectOutput>();
    @ViewChild("autocomplete")
    public readonly autocomplete: AutocompleteComponent<IMultiselectOptions>;

    @HostListener('click', ['$event'])
    public selectAllInputText(event: Event): void {
        this.autocomplete.input.input.nativeElement.select();
    }

    public selection: any;
    public filteredOptions: Observable<IMultiselectOptions[]>;
    public input: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public options: IMultiselectOptions[] = [];
    public optionsCache: IMultiselectOptions[] = [];
    public resultsCache: { [key: string]: IAirportSelectOutput } = {};
    public patchWith: string;
    public group: FormGroup = new FormGroup({
        location: new FormControl('')
    });
    public readonlyValue: string;
    public iconDefinition: IconDefinition;
    public event: EventEmitter<string> = new EventEmitter<string>();

    constructor(
        private readonly dictionarySearchRequestService: DictionarySearchRequestService,
        private readonly dictionarySearchResourceService: DictionarySearchResourceService,
        private readonly localizedPipe: LocalizedPipe,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
    }

    public ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges?.firstLocationCode?.currentValue === '') {
            this.resetAutocomplete();
        }
        if (simpleChanges?.firstLocationCode?.currentValue?.length > 0) {
            this.input.next(this.firstLocationCode);
        } else if (simpleChanges?.firstLocationCode) {
            // #29249: test added to reset content when click on 'new search'
            this.group.get('location').patchValue('');
        }
    }

    public ngOnInit(): void {
        this.setIconDefinition();
        if(!this.fieldConfig){
            this.setFieldConfig();
        }

        this.filteredOptions = this.input
            .asObservable()
            .pipe(
                debounceTime(100),
                // distinctUntilChanged(),
                switchMap((query: string): Observable<IMultiselectOptions[]> => this.dictionnarySearchStream(query)),
                tap((): void => {
                    this.initAutocompleteActiveOption();
                })
            );

        this.group.get('location')
            .valueChanges
            .pipe(
                filter((locationCode: IMultiselectOptions): boolean => Boolean(locationCode?.value))
            )
            .subscribe(
                (locationCode: IMultiselectOptions): void => {
                    this.onChooseLocation.emit(this.resultsCache[locationCode.value as string]);
                }
            );

        if (this.firstLocationCode) {
            this.event.emit(this.firstLocationCode);
            this.patchWith = this.firstIsAirportOnly ?
                `${this.firstLocationCode}-airport` :
                `${this.firstLocationCode}-city`;
        }
    }

    public dictionnarySearchStream(query: string): Observable<IMultiselectOptions[]> {
        if (query === "") {
            return of([]);
        }
        const request: RequestForLocationSearch = this.dictionarySearchRequestService.create(query);
        return this.dictionarySearchResourceService.locationSearch(request)
            .pipe(
                map((response: ResponseForLocationSearch): IMultiselectOptions[] =>
                    this.mapResult(response.resultArray as unknown as LocationSearchResultItem[])
                )
            );
    }

    public emitValueIfNoPanel($event: FocusEvent): void {
        const value: string = ($event.target as HTMLInputElement)?.value;

        const has3LetterValue: boolean = typeof (value) === 'string' && value.length === 3;
        if(!this.autocomplete.input.matAutocomplete.showPanel){
            if(!value){
                this.valueSelected(null);
            }else if(has3LetterValue){
                this.valueSelected({code: value ? value.toUpperCase(): null, isAirportOnly: false});
            }
        }
    }

    public displayDestination(selectedOption: IMultiselectOptions): string {
        return selectedOption?.label;
    }

    public valueSelected(selection: IAirportSelectOutput): void {
        this.onChooseLocation.emit(selection);
        this.selection = selection;
    }

    public changeInput(inputValue: string): void {
        this.input.next(inputValue);
    }

    public search(query: string): void {
        this.event.emit(query);
    }

    private resetAutocomplete(): void {
        this.group.get('location').reset();
        this.readonlyValue = null;
        this.changeDetectorRef.markForCheck();
    }

    private changeValue(value: string): void {
        this.event.emit(value);
        this.group.get('location').patchValue([value], {onlySelf: true, emitEvent: false});
    }

    private setIconDefinition(): void {
        const fromIcon: IconDefinition = this.customizedIconDefinition ? this.customizedIconDefinition.from : faPlaneDeparture;
        const toIcon: IconDefinition = this.customizedIconDefinition ? this.customizedIconDefinition.to : faPlaneArrival;
        const iconDefinition: IconDefinition = this.direction ?
            this.direction === 'from' ? fromIcon : toIcon :
            null;
        this.iconDefinition = iconDefinition;
    }
    private setFieldConfig(): void{
        this.fieldConfig = {
            formGroup: this.group,
            formControlName: 'location',
            tabindex: this.tabindex,
            className: "card-form-appearance",
            iconDefinition: this.iconDefinition,
            placeholder: Yii.t('wui-iqba-js', "Sélectionner")
        };
    }

    private initAutocompleteActiveOption(): void {

        if (this.resultsCache &&
            Object.entries(this.resultsCache).length > 0 &&
            this.firstLocationCode?.length > 0
            && this.group.get('location').value !== null) {
            const selectionEntry: [string, IAirportSelectOutput] = Object
                .entries(this.resultsCache)
                .find(([key, option]: [string, IAirportSelectOutput]): boolean => {
                    // search for airport only if required
                    if (this.firstIsAirportOnly) {
                        return key.includes(this.firstLocationCode) && key.includes('-airport');
                    }
                    // if city found, take it, otherwize search for airport
                    const cityFound: boolean = key.includes(this.firstLocationCode) && key.includes('-city');
                    if(cityFound){
                        return cityFound;
                    }
                    return key.includes(this.firstLocationCode) && key.includes('-airport');
                });
            if (selectionEntry) {
                const code: string = selectionEntry[0];
                this.group.get('location')
                    .patchValue(this.resultsCache[code].code);
                this.setFieldConfig();
                const selectedOption: IMultiselectOptions = this.optionsCache.find((option: IMultiselectOptions): boolean => option.value === code);
                this.readonlyValue = this.displayDestination(selectedOption);
                this.autocomplete.input.autocomplete.writeValue(selectedOption);
                this.changeDetectorRef.detectChanges();
            }
        }
    }

    private mapResult(resultArray: LocationSearchResultItem[]): IMultiselectOptions[] {
        const options: IMultiselectOptions[] = [];
        this.resultsCache = {};
        this.optionsCache = [];
        resultArray.forEach(
            (result: LocationSearchResultItem): void => {
                const faIcon: IconDefinition = this.computeIcon(result.location.locationtype);
                const value: string = `${result.location.code}-${result.location.locationtype}`;
                this.resultsCache[value] = {
                    code: result.location.code,
                    isAirportOnly: result.location.locationtype === 'airport'
                };
                const label: string = `${this.localizedPipe.transform(result.location.nameArray)} (${result.location.code})`;
                options.push(
                    {
                        value,
                        label,
                        faIcon,
                        secondLine: result.location.country?.nameArray ?
                            this.localizedPipe.transform(result.location.country.nameArray as TLocalizedString) :
                            null,
                    }
                );
                if (result.childArray?.length) {

                    result.childArray.forEach((location: LocationSearchLocationLocalized): void => {
                        const faIconChild: IconDefinition = this.computeIcon(location.locationtype);
                        const locationValue: string = `${location.code}-${location.locationtype}`;
                        this.resultsCache[locationValue] = {
                            code: location.code,
                            isAirportOnly: location.locationtype === 'airport'
                        };
                        const label: string = `${this.localizedPipe.transform(location.nameArray)} (${location.code})`;
                        options.push(
                            {
                                value: locationValue,
                                label,
                                faIcon: faIconChild,
                                isChild: true,
                            }
                        );
                    });
                }
            }
        );
        this.optionsCache = options;
        return options;
    }

    private computeIcon(locationtype: string): IconDefinition {
        switch (locationtype) {
            case 'city':
                return faCity;
            case 'airport':
            default:
                return faPlane;
        }
    }
}
